/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from 'lodash/get';
import React, { useMemo } from 'react';
import { decodeURIFromWidget } from '../../../../utils/decodeCustomCode';
import { AdService } from '../../../components/ad-service';
import { WidgetComponent } from '../../../components/Widget';
import WithArrowConfig from '../../hoc-components/with-arrow-config';
import '../custom-widget-row/widget.m.css';
import { useSelector } from 'react-redux';

// this will be the wrapper inside ahead
const arrowWrapper =
  Component =>
    ({ collection = {}, metadata = {}, index, collectionIndex }) => {
      const config = get(metadata, ['config'], {});
      let {
        adType = '',
        targetingId = '',
        widget = '',
        rowTargetingId = '',
        widgetHeightMobile = '',
        widgetHeightDesktop = ''
      } = config;

      const adComponent = (slotIndex = 0) => {
        const currentAdType = slotIndex === 0 ? adType : config[`slot${slotIndex + 1}AdType`];
        const currentTargetingId = slotIndex === 0 ? targetingId : config[`slot${slotIndex + 1}TargetingId`];
        return (
          <div className="app-ad app-ad--story-horizontal">
            <AdService
              adtype={currentAdType}
              adTargetingId={currentTargetingId}
              layoutName="PageBuilder"
              slotId={`PB-${get(metadata, ['rowId'], '')}${slotIndex > 0 ? `-slot${slotIndex + 1}` : ''}`}
            />
          </div>
        );
      };

      const preloadedCollection = useSelector(state =>
        get(state, ['qt', 'data', 'preloadedCollection'], null)
      );
      const firstTabSlugForRow = collection?.items?.find(item => item.type === 'collection')?.slug;
      const preloadedCollectionForRow = firstTabSlugForRow ? get(preloadedCollection, [firstTabSlugForRow], null) : null

      const lightRows = useSelector(state =>
        get(state, ['qt', 'config', 'pagebuilder-config', 'home', 'lightRows'], false)
      );
      const pageType = useSelector(state => get(state, ['qt', 'pageType'], ''));
      const shouldRenderWidgetWrapperBackground = useSelector(state =>
        get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'others', 'enableAdPlaceholderBackground'], false)
      );

      const getWidgetStyle = (slotIndex = 0) => {
        const currentWidgetHeightMobile = slotIndex === 0 ? widgetHeightMobile : config[`slot${slotIndex + 1}WidgetHeightMobile`];
        const currentWidgetHeightDesktop = slotIndex === 0 ? widgetHeightDesktop : config[`slot${slotIndex + 1}WidgetHeightDesktop`];
        return {
          '--widget-height-mobile': `${currentWidgetHeightMobile || 50}px`,
          '--widget-height-desktop': `${currentWidgetHeightDesktop || 50}px`
        };
      };

      const getWidgetId = (slotIndex = 0) => `widget-slot-${get(metadata, ['rowId'], '')}${slotIndex > 0 ? `-${slotIndex + 1}` : ''}`;

      // Memoized Widget Component
      const memoizedWidgetComp = (slotIndex = 0) => {
        let currentWidget = slotIndex === 0 ? widget : config[`slot${slotIndex + 1}Widget`];
        try {
          if (typeof window !== 'undefined') {
            currentWidget = window.atob(currentWidget);
          } else {
            const bufferedWidget = Buffer.from(currentWidget, 'base64');
            currentWidget = bufferedWidget.toString('utf-8');
          }
        } catch (e) {
          currentWidget = '';
        }
        const newId = `widget-${get(metadata, ['rowId'], '')}${slotIndex > 0 ? `-${slotIndex + 1}` : ''}`;
        const widgetComp = widgetId => {
          const updatedWidgetCode = decodeURIFromWidget(currentWidget).replace(/\[unique-id]/g, newId);
          return (
            <div className="app-ad app-ad--story-horizontal" styleName={`widget`} style={getWidgetStyle(slotIndex)}>
              <div id={widgetId}>
                <WidgetComponent widgetValue={updatedWidgetCode} />
              </div>
            </div>
          );
        };

        const memoizedWidget = useMemo(() => widgetComp(newId), [newId, currentWidget, shouldRenderWidgetWrapperBackground]);

        return <>{memoizedWidget}</>;
      };

      const widgetWrapper = (slotIndex = 0) => {
        return (
          <div className="app-ad app-ad--story-horizontal" styleName={`widget`} style={getWidgetStyle(slotIndex)}>
            <div id={getWidgetId(slotIndex)}></div>
          </div>
        );
      };

      const shouldRenderWidgetWrapper = lightRows && pageType === 'home-page';
      const updateComp = ({ type, slotIndex }) => {
        switch (type) {
          case 'ad':
            return () => adComponent(slotIndex);
          case 'widget':
            return shouldRenderWidgetWrapper ? () => widgetWrapper(slotIndex) : () => memoizedWidgetComp(slotIndex);
          default:
            return null;
        }
      };

      const updateAdAndWidgetComponent = (config = {}) => {
        if (!config.slotConfig) return config;
        config.slotConfig.forEach((slot, index) => {
          const { type } = slot;
          slot.component = updateComp({ type, slotIndex: index });
        });
        return config;
      };

      return (
        <div id={rowTargetingId || `row-${index}`} style={{ minHeight: '100%', maxWidth: '1172px', margin: 'auto' }}>
          {WithArrowConfig(Component)({ collection, config, index, updateAdAndWidgetComponent, collectionIndex, preloadedCollection: preloadedCollectionForRow })}
        </div>
      );
    };
export { arrowWrapper };
